/**
 * Which Supabase project this page talks to, with no side effects beyond
 * reading the hostname and the localhost preference. Shared by the app client
 * (supabase.ts) and the isolated P7 handoff page, whose own client must use
 * the same project and storage key but not the app's auto-refreshing client.
 */

// Supabase project configurations
const SUPABASE_CONFIGS = {
  production: {
    url: 'https://wzpdizjzvddtvzvwbihw.supabase.co',
    anonKey: 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Ind6cGRpemp6dmRkdHZ6dndiaWh3Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NjM0MDEwNzgsImV4cCI6MjA3ODk3NzA3OH0.7mfx-nXFWHnmlszeGai7dmk5X4BGSmgBCxhHekc-CeI',
  },
  demo: {
    url: 'https://fuxqvfyxgxybrilrlwqz.supabase.co',
    anonKey: 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImZ1eHF2Znl4Z3h5YnJpbHJsd3F6Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NjQxMzI5NTQsImV4cCI6MjA3OTcwODk1NH0.KrOG5Fm5-2EVu5GWoQpwJeb8iP28YYQ1MrVOQ6NMEvQ',
  },
};

// localStorage key for local development environment selection
export const LOCAL_ENV_KEY = 'local_supabase_env';

/**
 * Get the appropriate Supabase configuration based on hostname
 *
 * For localhost: checks localStorage for user's environment preference (set via login toggle)
 * For production hosts: uses hostname-based routing
 */
export function getSupabaseConfig(): { url: string; anonKey: string; env: 'demo' | 'production'; error?: string } {
  // Server-side rendering check
  if (typeof window === 'undefined') {
    // Default to demo for SSR/build time
    return { ...SUPABASE_CONFIGS.demo, env: 'demo' };
  }

  const hostname = window.location.hostname;

  // Demo environment (deployed)
  if (hostname === 'demo.hollywoodtrucks.com') {
    console.log('[Supabase] Using DEMO environment (hostname: demo.hollywoodtrucks.com)');
    return { ...SUPABASE_CONFIGS.demo, env: 'demo' };
  }

  // Production environment (deployed)
  if (hostname === 'app.hollywoodtrucks.com' || hostname === 'hollywoodtrucks.com') {
    console.log('[Supabase] Using PRODUCTION environment (hostname:', hostname, ')');
    return { ...SUPABASE_CONFIGS.production, env: 'production' };
  }

  // Generated production URLs remain unchanged. Actual previews must opt into
  // DEMO at build time; browser preferences must never select their project.
  if (hostname.endsWith('.vercel.app') && import.meta.env.VITE_DEPLOYMENT_ENV === 'preview') {
    if (import.meta.env.VITE_SUPABASE_ENVIRONMENT === 'demo') {
      return { ...SUPABASE_CONFIGS.demo, env: 'demo' };
    }
    return {
      url: 'https://preview-configuration-required.invalid',
      anonKey: 'preview-configuration-required',
      env: 'demo',
      error: 'This preview is not configured. Set VITE_SUPABASE_ENVIRONMENT=demo for the preview build and redeploy.',
    };
  }

  // Local development - check localStorage for user preference
  const localEnvPref = localStorage.getItem(LOCAL_ENV_KEY);

  if (localEnvPref === 'demo') {
    console.log('[Supabase] Using DEMO environment (localStorage preference, localhost)');
    return { ...SUPABASE_CONFIGS.demo, env: 'demo' };
  }

  // Default to production for local development
  console.log('[Supabase] Using PRODUCTION environment (localhost default)');
  return { ...SUPABASE_CONFIGS.production, env: 'production' };
}
